import React, { useEffect, useMemo, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { apiService } from '../services/apiService';
import SetupGuard from '../components/SetupGuard';
import { useAuth } from '../services/authContext';

const TYPES = ['exam', 'assignment', 'test', 'project'];
const emptyForm = { title: '', moduleId: '', classId: '', type: 'assignment', date: '', totalMarks: 100, description: '' };

const Exams = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [assessments, setAssessments] = useState([]);
  const [modules, setModules] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [assessmentData, moduleData, classData] = await Promise.all([
        apiService.getAll('assessments'),
        apiService.getAll('modules'),
        apiService.getAll('classes'),
      ]);
      setAssessments(assessmentData || []);
      setModules(moduleData || []);
      setClasses(classData || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load assessments.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredAssessments = useMemo(
    () =>
      assessments.filter(item =>
        [item.title, item.moduleName || item.subjectName, item.className, item.type]
          .join(' ')
          .toLowerCase()
          .includes(search.toLowerCase())
      ),
    [assessments, search]
  );

  const openAdd = () => {
    setCurrent(null);
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const openEdit = assessment => {
    setCurrent(assessment);
    setForm({
      title: assessment.title || '',
      moduleId: assessment.moduleId || assessment.subjectId || '',
      classId: assessment.classId || '',
      type: assessment.type || 'assignment',
      date: assessment.date || '',
      totalMarks: assessment.totalMarks || 100,
      description: assessment.description || '',
    });
    setIsModalOpen(true);
  };

  const handleChange = event => {
    const { name, value } = event.target;
    setForm(previous => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    try {
      setSaving(true);
      setError('');
      if (current) {
        await apiService.update('assessments', current._id, form);
      } else {
        await apiService.create('assessments', form);
      }
      setIsModalOpen(false);
      setForm(emptyForm);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save assessment.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this assessment?')) return;
    try {
      await apiService.delete('assessments', id);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete assessment.');
    }
  };

  const tableContent = (
    <div className="table-responsive">
      <table className="data-table">
        <thead>
          <tr><th>Title</th><th>Module</th><th>Class</th><th>Type</th><th>Date</th><th>Total</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {filteredAssessments.length === 0 ? (
            <tr><td colSpan={7} className="no-data-message">No assessments found.</td></tr>
          ) : filteredAssessments.map(item => (
            <tr key={item._id}>
              <td><strong>{item.title}</strong></td>
              <td>{item.moduleName || item.subjectName}</td>
              <td>{item.className}</td>
              <td>{item.type}</td>
              <td>{new Date(item.date).toLocaleDateString()}</td>
              <td>{item.totalMarks}</td>
              <td>
                <div className="action-buttons">
                  <button className="btn-icon" onClick={() => openEdit(item)} title="Edit">Edit</button>
                  <button className="btn-icon" style={{ color: '#7B1FA2' }} onClick={() => window.location.href=`/grades?assessmentId=${item._id}`}>Grades</button>
                  <button className="btn-icon" onClick={() => handleDelete(item._id)} title="Delete">Delete</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) return <LoadingSpinner message="Loading assessments..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{isAdmin ? 'Assessments' : 'My Assessments'}</h1>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Assessment</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isAdmin ? (
        <SetupGuard checks={[
          {
            ok: classes.length > 0,
            icon: '🏫',
            label: 'No Classes exist yet',
            description: 'Create a class or batch before adding assessments.',
            action: { label: 'Create Class', to: '/programmes' },
          },
          {
            ok: modules.length > 0,
            icon: '📚',
            label: 'No Modules exist yet',
            description: 'Create modules and assign them to classes before creating assessments.',
            action: { label: 'Create Module', to: '/modules' },
          },
        ]}>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search assessments..."
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
          </div>
          {tableContent}
        </SetupGuard>
      ) : (
        <>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search assessments..."
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
          </div>
          {tableContent}
        </>
      )}

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={current ? 'Edit Assessment' : 'Add Assessment'}>
        <form onSubmit={handleSubmit} className="form">
          <div className="form-group">
            <label>Title</label>
            <input name="title" value={form.title} onChange={handleChange} required />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Module</label>
              <select name="moduleId" value={form.moduleId} onChange={handleChange} required>
                <option value="">Select Module</option>
                {modules.map(module => <option key={module._id} value={module._id}>{module.code} - {module.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Class</label>
              <select name="classId" value={form.classId} onChange={handleChange} required>
                <option value="">Select Class</option>
                {classes.map(item => <option key={item._id} value={item._id}>{item.name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Type</label>
              <select name="type" value={form.type} onChange={handleChange}>
                {TYPES.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Date</label>
              <input type="date" name="date" value={form.date} onChange={handleChange} required />
            </div>
          </div>

          <div className="form-group">
            <label>Total Marks</label>
            <input type="number" name="totalMarks" value={form.totalMarks} onChange={handleChange} min={1} required />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} rows={4} />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : current ? 'Update Assessment' : 'Add Assessment'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Exams;
